/* eslint-disable react/display-name */
//React
import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import PropTypes from "prop-types";
import Select from "react-select";
import Creatable from "react-select/creatable";
//CSS
import "./inputs.scss";

const defaultOptions = [];

const SelectField = forwardRef(
	(
		{
			name,
			options,
			required,
			disabled,
			label,
			multi,
			info,
			error,
			search,
			selected,
			placeholder,
			onSubmit,
			customClasses,
			clear,
			clearCallback,
			reset,
			defaultValue,
			width = 'long',
			creatable = false,
			menuPortal = false,
			// react-select pass-throughs, creatable only. Undefined keeps
			// react-select's own defaults, so every existing consumer is unchanged.
			// `isValidNewOption` is how a caller suppresses "Add ..." for a value
			// that already exists; `filterOption` is how it decides what counts as a
			// match while typing.
			isValidNewOption,
			filterOption
		},
		ref
	) => {
		const [selectedOption, setSelectedOption] = useState(selected ?? null);
		const [inputValue, setInputValue] = useState("");

		// Sync internal state when selected prop changes from parent
		useEffect(() => {
			setSelectedOption(selected ?? null);
		}, [selected]);

		const handleInputChange = (value, action) => {
			if (action.action === "input-change" || action.action === "menu-close") {
				setInputValue(value);
			}
		};

		useImperativeHandle(ref, () => ({
			clearSelect() {
				setSelectedOption([]);
				onSubmit([]);
			},
			select(sel) {
				setSelectedOption(sel);
			},
		}));

		const handleChange = (selected, actionMeta) => {
			setSelectedOption(selected);
			onSubmit(selected);

			// Clear input if a new option was created (not selected from results)
			if (actionMeta?.action === "create-option") {
				setInputValue("");
			}
		};

		const wrapperClasses = [
			"input-wrapper",
			error ? "error" : "valid",
			required ? "required" : "not-required",
			disabled ? "disabled" : "enabled",
			search ? "search" : "standard",
			customClasses,
		];
		return (
			<div className={wrapperClasses.join(" ")}>
				{label ?? false ? <label>{label}</label> : ""}
				{clear && (
					<button
						type="button"
						className="clear-select btn-simple"
						onClick={() => [
							setSelectedOption([]),
							onSubmit([]),
							clearCallback ? clearCallback() : "",
						]}
					>
						Clear
					</button>
				)}
				{reset && (
					<button
						type="button"
						className="clear-select btn-simple"
						onClick={() => [
							setSelectedOption(defaultValue),
							onSubmit(defaultValue),
						]}
					>
						Reset
					</button>
				)}
				{creatable ? (
					<Creatable
						value={selectedOption ?? []}
						onChange={handleChange}
						options={options ?? defaultOptions}
						className={`select-field ${width}`}
						classNamePrefix="select"
						classNames={{
							option: (state) => (state.isSelected ? "selected" : "unselected"),
						}}
						name={name}
						isDisabled={disabled}
						required={required}
						isMulti={multi}
						isSearchable={search}
						hideSelectedOptions={false}
						menuPlacement="auto"
						menuPortalTarget={menuPortal ? document.body : undefined}
						styles={menuPortal ? { menuPortal: (base) => ({ ...base, zIndex: 10100 }) } : undefined}
						closeMenuOnSelect={!multi}
						placeholder={placeholder ?? "Select..."}
						inputValue={inputValue}
						onInputChange={handleInputChange}
						isValidNewOption={isValidNewOption}
						filterOption={filterOption}
						formatCreateLabel={(val) => `Add "${val}"`}
						theme={(theme) => ({
							...theme,
							borderRadius: 0,
							colors: {
								...theme.colors,
								primary25: "#B3CF79",
								primary: "#9ACA3C",
							},
						})}
					/>
				) : (
					<Select
						value={selectedOption ?? []}
						onChange={handleChange}
						options={options ?? defaultOptions}
						className={`select-field ${width}`}
						classNamePrefix="select"
						classNames={{
							option: (state) => (state.isSelected ? "selected" : "unselected"),
						}}
						name={name}
						isDisabled={disabled}
						required={required}
						isMulti={multi}
						isSearchable={search}
						hideSelectedOptions={false}
						menuPlacement="auto"
						menuPortalTarget={menuPortal ? document.body : undefined}
						styles={menuPortal ? { menuPortal: (base) => ({ ...base, zIndex: 10100 }) } : undefined}
						closeMenuOnSelect={!multi}
						placeholder={placeholder ?? "Select..."}
						inputValue={inputValue}
						onInputChange={handleInputChange}
						theme={(theme) => ({
							...theme,
							borderRadius: 0,
							colors: {
								...theme.colors,
								primary25: "#B3CF79",
								primary: "#9ACA3C",
							},
						})}
					/>
				)}
				<div className="input--inner"></div>
				{info ?? false ? <div className="input-info">{info}</div> : ""}
			</div>
		);
	}
);


SelectField.propTypes = {
	isValidNewOption: PropTypes.func,
	filterOption: PropTypes.func,
	name: PropTypes.string.isRequired,
	label: PropTypes.string,
	required: PropTypes.bool.isRequired,
	disabled: PropTypes.bool.isRequired,
	multi: PropTypes.bool,
	info: PropTypes.string,
	search: PropTypes.bool,
	error: PropTypes.bool,
	options: PropTypes.array,
	placeholder: PropTypes.string,
	onSubmit: PropTypes.func.isRequired,
	handleChange: PropTypes.func,
	selected: PropTypes.any,
	value: PropTypes.any,
	customClasses: PropTypes.string,
	clear: PropTypes.bool,
	clearCallback: PropTypes.func,
	reset: PropTypes.bool,
	defaultValue: PropTypes.any,
	width: PropTypes.oneOf(['long', 'medium', 'short']),
	creatable: PropTypes.bool,
	menuPortal: PropTypes.bool
};

export default SelectField;